import {
  memo, useEffect, useMemo, useRef, useState,
} from '../../lib/teact/teact';

import buildClassName from '../../util/buildClassName';

import useLastCallback from '../../hooks/useLastCallback';

import Icon from '../common/icons/Icon';
import Switcher from '../ui/Switcher';

import styles from './ProtocolEditorModal.module.scss';

export interface ProtocolItem {
  id?: number;
  tenant_id?: number;
  title: string;
  slug?: string;
  description?: string;
  triggers: string[];
  content_md: string;
  is_active: boolean;
  order_index?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CatalogItem {
  tag: string;
  name: string;
  description: string;
  type: 'variable' | 'action';
  example?: string;
  params?: Record<string, string>;
  returns?: string;
}

export interface CatalogCategory {
  id: string;
  category: string;
  description: string;
  items: CatalogItem[];
}

type OwnProps = {
  isOpen: boolean;
  protocol?: ProtocolItem;
  catalog: CatalogCategory[];
  isSaving?: boolean;
  onClose: NoneToVoidFunction;
  onSave: (payload: ProtocolItem) => void;
};

const DEFAULT_PROCEDURE = `# Procedimiento de Atención

## Objetivo:
Orientar cordialmente al estudiante sobre los pasos requeridos.

## Pasos:
1. Saludar afectuosamente llamándolo por {{contacto.nombre}}.
2. Si aún no confirmó su identidad, solicitar Cédula y consultar con {{helix.buscar_persona}}.
3. Explicar los requisitos oficiales de su programa {{contacto.programa_actual}}.
4. Si el caso requiere intervención de la coordinación, derivar con {{crm.derivar_a_humano}}.`;

const ProtocolEditorModal = ({
  isOpen,
  protocol,
  catalog,
  isSaving,
  onClose,
  onSave,
}: OwnProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [triggers, setTriggers] = useState<string[]>([]);
  const [contentMd, setContentMd] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [newTriggerInput, setNewTriggerInput] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [copiedTag, setCopiedTag] = useState<string | undefined>(undefined);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  const editorRef = useRef<HTMLTextAreaElement>();

  useEffect(() => {
    if (protocol) {
      setTitle(protocol.title || '');
      setDescription(protocol.description || '');
      setTriggers(Array.isArray(protocol.triggers) ? [...protocol.triggers] : []);
      setContentMd(protocol.content_md || '');
      setIsActive(protocol.is_active ?? true);
    } else {
      setTitle('');
      setDescription('');
      setTriggers(['consulta tramites', 'requisitos entrega']);
      setContentMd(DEFAULT_PROCEDURE);
      setIsActive(true);
    }
    setValidationError(undefined);
    setPreviewMode(false);
    setSearchQuery('');
    setSelectedCategory('ALL');
  }, [protocol, isOpen]);

  const handleAddTrigger = useLastCallback(() => {
    const text = newTriggerInput.trim().toLowerCase();
    if (text && !triggers.includes(text)) {
      setTriggers((prev) => [...prev, text]);
      setNewTriggerInput('');
    }
  });

  const handleTriggerKeyDown = useLastCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTrigger();
    }
  });

  const handleRemoveTrigger = useLastCallback((index: number) => {
    setTriggers((prev) => prev.filter((_, i) => i !== index));
  });

  const insertTagIntoEditor = useLastCallback((tag: string) => {
    const textarea = editorRef.current;
    if (!textarea) {
      setContentMd((prev) => `${prev} ${tag} `);
      return;
    }

    const start = textarea.selectionStart ?? contentMd.length;
    const end = textarea.selectionEnd ?? contentMd.length;
    const before = contentMd.substring(0, start);
    const after = contentMd.substring(end);

    setContentMd(`${before}${tag}${after}`);

    requestAnimationFrame(() => {
      textarea.focus();
      const newCursorPos = start + tag.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    });
  });

  const copyToClipboard = useLastCallback(async (tag: string) => {
    try {
      await navigator.clipboard.writeText(tag);
      setCopiedTag(tag);
      setTimeout(() => {
        setCopiedTag(undefined);
      }, 2000);
    } catch {
      // Fallback
    }
  });

  const totalCatalogItems = useMemo(() => {
    return catalog.reduce((acc, cat) => acc + cat.items.length, 0);
  }, [catalog]);

  const filteredCatalog = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    return catalog
      .map((cat) => {
        if (selectedCategory !== 'ALL' && cat.id !== selectedCategory) {
          return undefined;
        }

        const matchingItems = cat.items.filter((item) => {
          if (!query) return true;
          return (
            item.tag.toLowerCase().includes(query)
            || item.name.toLowerCase().includes(query)
            || item.description.toLowerCase().includes(query)
          );
        });

        if (matchingItems.length === 0) return undefined;

        return {
          ...cat,
          items: matchingItems,
        };
      })
      .filter((cat): cat is CatalogCategory => Boolean(cat));
  }, [catalog, searchQuery, selectedCategory]);

  const handleSubmit = useLastCallback(() => {
    if (!title.trim()) {
      setValidationError('Debes ingresar un título para el protocolo.');
      return;
    }
    if (triggers.length === 0) {
      setValidationError('Debes agregar al menos una frase disparadora.');
      return;
    }
    if (!contentMd.trim() || contentMd.length < 10) {
      setValidationError('El procedimiento debe tener instrucciones descriptivas (mínimo 10 caracteres).');
      return;
    }

    setValidationError(undefined);
    onSave({
      id: protocol?.id,
      title: title.trim(),
      slug: protocol?.slug,
      description: description.trim() || undefined,
      triggers,
      content_md: contentMd.trim(),
      is_active: isActive,
    });
  });

  if (!isOpen) {
    return undefined;
  }

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <div className={styles.headerIcon}>
              <Icon name="ai" />
            </div>
            <div>
              <h2 className={styles.headerTitle}>
                {protocol ? 'Editar Protocolo de Atención' : 'Nuevo Protocolo de Atención'}
              </h2>
              <p className={styles.headerSubtitle}>
                Redacta el procedimiento paso a paso e inserta variables o APIs entre llaves dobles.
              </p>
            </div>
          </div>

          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Cerrar">
            <Icon name="close" />
          </button>
        </div>

        {/* Validation Alert */}
        {validationError && (
          <div className={styles.alertError}>
            <span>⚠️</span>
            <span>{validationError}</span>
          </div>
        )}

        {/* Body Split */}
        <div className={styles.bodySplit}>
          {/* Left Column: Editor & Config */}
          <div className={styles.leftColumn}>
            {/* Row 1: Title & State */}
            <div className={styles.formRowGrid}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>
                  Título del Protocolo
                  {' '}
                  <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={styles.input}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ej. Soporte de Acceso a la Plataforma"
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label}>Estado</label>
                <div className={styles.stateToggle}>
                  <Switcher
                    id="protocol-modal-status-switcher"
                    label="Estado del protocolo"
                    checked={isActive}
                    onCheck={setIsActive}
                  />
                  <span className={isActive ? styles.stateTextActive : styles.stateTextPaused}>
                    {isActive ? 'Activo' : 'Pausado'}
                  </span>
                </div>
              </div>
            </div>

            {/* Row 2: Description */}
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Descripción Breve (Opcional)</label>
              <input
                type="text"
                className={styles.input}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ej. Aplica a cualquier usuario que no pueda ingresar o haya olvidado su clave"
              />
            </div>

            {/* Row 3: Triggers */}
            <div className={styles.fieldGroup}>
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <label className={styles.label}>
                  Disparadores / Frases Clave
                  {' '}
                  <span className={styles.required}>*</span>
                </label>
                <span style="font-size: 0.6875rem; color: var(--color-text-secondary);">
                  Presiona Enter para agregar
                </span>
              </div>

              <div className={styles.triggersBox}>
                {triggers.map((trig, idx) => (
                  <span key={trig} className={styles.triggerChip}>
                    {trig}
                    <button
                      type="button"
                      className={styles.triggerRemove}
                      onClick={() => handleRemoveTrigger(idx)}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  className={styles.triggerInput}
                  value={newTriggerInput}
                  onChange={(e) => setNewTriggerInput(e.target.value)}
                  onKeyDown={handleTriggerKeyDown}
                  placeholder="Escribe una frase y presiona Enter..."
                />
              </div>
            </div>

            {/* Row 4: Markdown Procedure */}
            <div className={styles.fieldGroup} style="flex: 1; display: flex; flex-direction: column;">
              <div className={styles.editorTabsHeader}>
                <label className={styles.label}>Procedimiento en Markdown</label>

                <div className={styles.tabGroup}>
                  <button
                    type="button"
                    className={buildClassName(styles.tabBtn, !previewMode && styles.tabBtnActive)}
                    onClick={() => setPreviewMode(false)}
                  >
                    <Icon name="edit" />
                    <span>Editor</span>
                  </button>
                  <button
                    type="button"
                    className={buildClassName(styles.tabBtn, previewMode && styles.tabBtnActive)}
                    onClick={() => setPreviewMode(true)}
                  >
                    <Icon name="eye" />
                    <span>Vista Previa</span>
                  </button>
                </div>
              </div>

              <div className={styles.editorWrapper}>
                {!previewMode ? (
                  <textarea
                    ref={editorRef}
                    className={styles.editorTextarea}
                    value={contentMd}
                    onChange={(e) => setContentMd(e.target.value)}
                    placeholder="Escribe las instrucciones paso a paso para la IA..."
                  />
                ) : (
                  <div className={styles.previewContainer}>
                    {contentMd}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Catalog */}
          <div className={styles.rightColumn}>
            <div className={styles.catalogHeader}>
              <span className={styles.catalogTitle}>Catálogo de Variables & APIs</span>
              <span className={styles.catalogBadge}>
                {totalCatalogItems}
                {' '}
                disponibles
              </span>
            </div>
            <p className={styles.catalogSubtitle}>
              Haz clic en
              {' '}
              <strong>Insertar</strong>
              {' '}
              para colocar la variable donde está tu cursor.
            </p>

            <input
              type="text"
              className={styles.catalogSearch}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar variable o API..."
            />

            {/* Category Filter Pills */}
            <div className={styles.categoryFilterBar}>
              <button
                type="button"
                className={buildClassName(
                  styles.categoryPill,
                  selectedCategory === 'ALL' && styles.categoryPillActive,
                )}
                onClick={() => setSelectedCategory('ALL')}
              >
                Todas
              </button>
              {catalog.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={buildClassName(
                    styles.categoryPill,
                    selectedCategory === cat.id && styles.categoryPillActive,
                  )}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  {cat.category.split(' ')[0]}
                </button>
              ))}
            </div>

            {/* Variable Cards List */}
            <div className={styles.catalogList}>
              {filteredCatalog.map((cat) => (
                <div key={cat.id} style="display: flex; flex-direction: column; gap: 0.5rem;">
                  <div className={styles.label}>
                    {cat.category}
                  </div>

                  {cat.items.map((item) => (
                    <div key={item.tag} className={styles.catalogCard}>
                      <div className={styles.cardTop}>
                        <span className={styles.cardTag}>{item.tag}</span>

                        <div className={styles.cardActions}>
                          <button
                            type="button"
                            className={styles.btnCopy}
                            title="Copiar al portapapeles"
                            onClick={() => copyToClipboard(item.tag)}
                          >
                            <Icon name={copiedTag === item.tag ? 'check' : 'copy'} />
                          </button>

                          <button
                            type="button"
                            className={styles.btnInsert}
                            onClick={() => insertTagIntoEditor(item.tag)}
                          >
                            <Icon name="arrow-left" />
                            <span>Insertar</span>
                          </button>
                        </div>
                      </div>

                      <span className={styles.cardName}>{item.name}</span>
                      <p className={styles.cardDescription}>{item.description}</p>

                      {item.returns && (
                        <span className={styles.cardReturns}>
                          Entrega:
                          {' '}
                          {item.returns}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {filteredCatalog.length === 0 && (
                <div
                  style="text-align: center; padding: 2rem; color: var(--color-text-secondary); font-size: 0.75rem;"
                >
                  No se encontraron variables con ese criterio.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <span className={styles.footerNote}>
            Los cambios se aplicarán inmediatamente a los mensajes entrantes de Telegram.
          </span>

          <div className={styles.footerButtons}>
            <button type="button" className={styles.btnCancel} onClick={onClose}>
              Cancelar
            </button>
            <button
              type="button"
              className={styles.btnSave}
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? 'Guardando...' : protocol ? 'Actualizar Protocolo' : 'Crear Protocolo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ProtocolEditorModal);
